import React, { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { motion } from "framer-motion";
import { 
  AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineFullscreen, 
  AiOutlineSearch, AiOutlinePrinter, AiOutlineDownload,
  AiOutlineHighlight, AiOutlineSave, AiOutlineMenu
} from "react-icons/ai";
import { BsZoomIn, BsZoomOut } from "react-icons/bs";
import { FaRegHandPaper, FaRegCommentAlt } from "react-icons/fa";
import { MdOutlineRotateLeft, MdOutlineRotateRight } from "react-icons/md";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

function PdfComp({ documentUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.8);
  const [rotation, setRotation] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentTool, setCurrentTool] = useState("hand");
  const [highlights, setHighlights] = useState([]);
  const [comments, setComments] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  const calculatePageSize = useCallback((containerWidth, containerHeight) => {
    const margin = windowWidth <= 768 ? 20 : 40;
    const aspectRatio = 1 / Math.sqrt(2);
    
    const maxWidth = containerWidth - 2 * margin;
    const maxHeight = containerHeight - 2 * margin;

    let width = maxWidth;
    let height = width / aspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    return { width, height };
  }, [windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setPageSize(calculatePageSize(width, height));
      }
      setScale(newWidth <= 768 ? 1.2 : 1.8);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [calculatePageSize]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const changePage = (offset) => {
    setPageNumber((prevPageNumber) => Math.min(Math.max(prevPageNumber + offset, 1), numPages));
  };

  const zoomIn = () => setScale((prevScale) => Math.min(prevScale + 0.1, 3));
  const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
  const rotateLeft = () => setRotation((prevRotation) => (prevRotation - 90) % 360);
  const rotateRight = () => setRotation((prevRotation) => (prevRotation + 90) % 360);

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleSearch = () => {
    console.log("Searching for:", searchText);
    // Implement PDF text search functionality here
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = documentUrl;
    link.download = "document.pdf";
    link.click();
  };

  const handleHighlight = () => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const pageRect = pageRef.current.getBoundingClientRect();

    const highlight = {
      x: (rect.left - pageRect.left) / scale,
      y: (rect.top - pageRect.top) / scale,
      width: rect.width / scale,
      height: rect.height / scale,
      pageNumber: pageNumber,
    };

    setHighlights([...highlights, highlight]);
  };

  const handleComment = () => {
    const comment = prompt("Enter your comment:");
    if (comment) {
      const newComment = {
        text: comment,
        x: 50,
        y: 50,
        pageNumber: pageNumber,
      };
      setComments([...comments, newComment]);
    }
  };

  const handleSave = () => {
    console.log("Saving highlights:", highlights);
    console.log("Saving comments:", comments);
    alert("Changes saved!");
  };

  const renderToolbar = () => (
    <motion.div 
      className={`flex flex-wrap justify-between items-center bg-gray-800 text-white p-2 mb-2 ${windowWidth <= 768 ? 'flex-col' : ''}`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {windowWidth <= 768 && (
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="w-full mb-2 p-2 bg-gray-700 rounded"
        >
          <AiOutlineMenu /> {isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
        </button>
      )}
      {(windowWidth > 768 || isMobileMenuOpen) && (
        <>
          <div className={`flex flex-wrap space-x-1 ${windowWidth <= 768 ? 'w-full justify-center mb-2' : ''}`}>
            <ToolbarButton icon={<FaRegHandPaper />} onClick={() => setCurrentTool("hand")} isActive={currentTool === "hand"} tooltip="Hand Tool" />
            <ToolbarButton icon={<AiOutlineHighlight />} onClick={() => setCurrentTool("highlight")} isActive={currentTool === "highlight"} tooltip="Highlight Tool" />
            <ToolbarButton icon={<FaRegCommentAlt />} onClick={() => setCurrentTool("comment")} isActive={currentTool === "comment"} tooltip="Comment Tool" />
            <ToolbarButton icon={<BsZoomIn />} onClick={zoomIn} tooltip="Zoom In" />
            <ToolbarButton icon={<BsZoomOut />} onClick={zoomOut} tooltip="Zoom Out" />
          </div>
          <div className={`flex flex-wrap space-x-1 ${windowWidth <= 768 ? 'w-full justify-center mb-2' : ''}`}>
            <ToolbarButton icon={<MdOutlineRotateLeft />} onClick={rotateLeft} tooltip="Rotate Left" />
            <ToolbarButton icon={<MdOutlineRotateRight />} onClick={rotateRight} tooltip="Rotate Right" />
            <ToolbarButton icon={<AiOutlineFullscreen />} onClick={toggleFullScreen} tooltip="Toggle Fullscreen" />
            <ToolbarButton icon={<AiOutlinePrinter />} onClick={handlePrint} tooltip="Print" />
            <ToolbarButton icon={<AiOutlineDownload />} onClick={handleDownload} tooltip="Download" />
            <ToolbarButton icon={<AiOutlineSave />} onClick={handleSave} tooltip="Save Changes" />
          </div>
          <div className={`flex items-center space-x-2 ${windowWidth <= 768 ? 'w-full justify-center mt-2' : ''}`}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="bg-gray-700 text-white px-2 py-1 rounded w-full sm:w-auto"
            />
            <ToolbarButton icon={<AiOutlineSearch />} onClick={handleSearch} tooltip="Search" />
          </div>
        </>
      )}
    </motion.div>
  );

  const renderHighlights = () => (
    highlights
      .filter(h => h.pageNumber === pageNumber)
      .map((highlight, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `${highlight.x}px`,
            top: `${highlight.y}px`,
            width: `${highlight.width}px`,
            height: `${highlight.height}px`,
            backgroundColor: 'rgba(255, 255, 0, 0.3)',
            pointerEvents: 'none',
          }}
        />
      ))
  );

  const renderComments = () => (
    comments
      .filter(c => c.pageNumber === pageNumber)
      .map((comment, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `${comment.x}px`,
            top: `${comment.y}px`,
            backgroundColor: 'white',
            border: '1px solid black',
            padding: '5px',
            borderRadius: '5px',
          }}
        >
          {comment.text}
        </div>
      ))
  );

  return (
    <div ref={containerRef} className="pdf-viewer h-full flex flex-col bg-gray-100 p-1 sm:p-4 rounded-lg shadow-lg">
      <h1 className="text-lg sm:text-2xl font-bold text-center mb-2 sm:mb-4">intelleX File Viewer</h1>
      {renderToolbar()}
      <div className="flex justify-between items-center mb-2 sm:mb-4">
        <button
          onClick={() => changePage(-1)}
          disabled={pageNumber <= 1}
          className="bg-blue-500 text-white px-2 sm:px-4 py-1 sm:py-2 rounded disabled:opacity-50 text-sm sm:text-base"
        >
          <AiOutlineArrowLeft />
        </button>
        <p className="text-xs sm:text-base">
          Page {pageNumber} of {numPages}
        </p>
        <button
          onClick={() => changePage(1)}
          disabled={pageNumber >= numPages}
          className="bg-blue-500 text-white px-2 sm:px-4 py-1 sm:py-2 rounded disabled:opacity-50 text-sm sm:text-base"
        >
          <AiOutlineArrowRight />
        </button>
      </div>
      <div className="flex-grow overflow-auto flex justify-center items-start bg-gray-300 rounded-lg p-1 sm:p-4">
        <div ref={pageRef} className="relative bg-white shadow-lg">
          <Document file={documentUrl} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              pageNumber={pageNumber}
              width={pageSize.width}
              scale={scale}
              rotate={rotation}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onMouseUp={() => {
                if (currentTool === 'highlight') handleHighlight();
                if (currentTool === 'comment') handleComment();
              }}
            />
          </Document>
          {renderHighlights()}
          {renderComments()}
        </div>
      </div>
    </div>
  );
}

const ToolbarButton = ({ icon, onClick, isActive, tooltip }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className={`p-1 sm:p-2 rounded text-xs sm:text-sm ${isActive ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
    title={tooltip}
  >
    {icon}
  </motion.button>
);

export default PdfComp;